/**
 * AudioPreviewHook - Handles audio preview functionality for sound selection
 *
 * Features:
 * - Automatically plays sound when selection changes
 * - Manual play button functionality
 * - Volume control
 * - Stops previous audio when new one starts
 */
export const AudioPreviewHook = {
  mounted() {
    this.currentAudio = null;
    // Cache DOM elements to avoid repeated queries
    this.cachedElements = {};
    this.setupEventListeners();
  },

  destroyed() {
    this.stopCurrentAudio();
    // Clear cached elements
    this.cachedElements = {};
  },

  setupEventListeners() {
    // Use single event listener with delegation for better performance
    this.handleEvent = (event) => {
      const target = event.target;

      // Handle sound selection changes
      if (
        event.type === "change" &&
        target.matches("select[data-sound-select]")
      ) {
        this.handleSoundChange(event);
        return;
      }

      // Handle volume changes (both input and select)
      if (
        (event.type === "input" || event.type === "change") &&
        (target.matches("input[data-volume-control]") ||
          target.matches("select[data-volume-control]"))
      ) {
        this.handleVolumeChange(event);
        return;
      }

      // Handle play button clicks
      if (
        event.type === "click" &&
        (target.matches("button[data-play-sound]") ||
          target.closest("button[data-play-sound]"))
      ) {
        event.preventDefault();
        this.handlePlayButton(event);
      }
    };

    // Single event listener for all events
    this.el.addEventListener("change", this.handleEvent);
    this.el.addEventListener("input", this.handleEvent);
    this.el.addEventListener("click", this.handleEvent);
  },

  handleSoundChange(event) {
    const soundFile = event.target.value;

    if (soundFile) {
      this.playSound(soundFile, event.target);
    }
  },

  handleVolumeChange(event) {
    const volumePercent = parseInt(event.target.value);
    const audioVolume = this.mapVolumeToAudio(volumePercent);

    if (this.currentAudio) {
      this.currentAudio.volume = audioVolume;
    }

    // Play the sound with the new volume
    const soundSelect = this.el.querySelector("select[data-sound-select]");
    if (soundSelect && soundSelect.value) {
      this.playSound(soundSelect.value, soundSelect);
    }
  },

  handlePlayButton(event) {
    const button =
      event.target.closest("button[data-play-sound]") || event.target;
    const soundFile = button.dataset.soundFile;

    if (soundFile) {
      this.playSound(soundFile, button);
    }
  },

  playSound(soundFile, sourceEl = null) {
    this.stopCurrentAudio();

    try {
      const audioPath = this.resolveAudioPath(soundFile, sourceEl);

      if (!audioPath) {
        return;
      }
      const audio = new Audio(audioPath);
      this.currentAudio = audio;

      // Cache volume control element
      const volumeControlKey = "volumeControl";
      if (!this.cachedElements[volumeControlKey]) {
        this.cachedElements[volumeControlKey] =
          this.el.querySelector("input[data-volume-control]") ||
          this.el.querySelector("select[data-volume-control]");
      }

      const volumeControl = this.cachedElements[volumeControlKey];
      if (volumeControl) {
        const volumePercent = parseInt(volumeControl.value);
        const audioVolume = this.mapVolumeToAudio(volumePercent);
        audio.volume = audioVolume;
      } else if (sourceEl && sourceEl.dataset && sourceEl.dataset.volume) {
        const volumePercent = parseInt(sourceEl.dataset.volume);
        const audioVolume = this.mapVolumeToAudio(volumePercent);
        audio.volume = audioVolume;
      } else {
        audio.volume = this.mapVolumeToAudio(100);
      }

      // Use arrow functions to maintain context and avoid bind
      const errorHandler = (e) => {
        console.error("Audio playback error:", e);
        console.error("Failed to load audio from:", audioPath);
      };

      const endedHandler = () => {
        if (this.currentAudio === audio) {
          this.currentAudio = null;
        }
      };

      // Add event listeners
      audio.addEventListener("error", errorHandler);
      audio.addEventListener("ended", endedHandler);

      const playPromise = audio.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch((error) => {
          console.error("Audio playback error:", error);
        });
      }
    } catch (error) {
      console.error("Failed to create audio:", error);
    }
  },

  resolveAudioPath(soundFile, sourceEl) {
    if (!soundFile) return null;

    if (soundFile.startsWith("http://") || soundFile.startsWith("https://")) {
      return soundFile;
    }

    if (soundFile.startsWith("/")) {
      return soundFile;
    }

    const base =
      (sourceEl && sourceEl.dataset.soundBase) ||
      (this.el &&
        this.el.querySelector("select[data-sound-select]")?.dataset
          .soundBase) ||
      null;

    if (base) {
      const encoded = soundFile
        .split("/")
        .map((part) => encodeURIComponent(part))
        .join("/");
      return `${base}/${encoded}`;
    }

    return `/audio/${soundFile}`;
  },

  stopCurrentAudio() {
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio.currentTime = 0;
      this.currentAudio = null;
    }
  },

  // Map volume percentage (50-300) to audio volume (0.1-1.0)
  // 300% -> 1.0, 275% -> 0.9, 250% -> 0.8, ..., 50% -> 0.1, 25% -> 0.1 (minimum)
  mapVolumeToAudio(volumePercent) {
    // Clamp to valid range
    const clampedPercent = Math.max(25, Math.min(300, volumePercent));

    // Calculate step (0-11, where 0 = 300%, 11 = 25%)
    const step = Math.floor((300 - clampedPercent) / 25);

    // Map to audio volume (1.0 down to 0.1)
    const audioVolume = Math.max(0.1, 1.0 - step * 0.1);

    return audioVolume;
  },
};
